"use client";

/**
 * FilePreviewOverlay — the universal file viewer, as a POPUP (founder ruling
 * 08-04, mockups/file-preview-composer-2026-08-04.html is the contract).
 *
 * Click any file in any folder → a contained card floating over the page: a
 * metadata header (kind glyph tile + filename + the file's story in one line)
 * with slim pill actions on the right, and the file's content filling the card
 * below it. No scrim, no blur — the page stays crisp behind the card and the
 * composer band at the bottom stays visible and usable. Esc, outside click,
 * and ✕ all close. Every surface reads from the theme tokens, never hardcoded
 * dark. Mounted once in AppShell; opens from the filesStore so every grid
 * (folders, Media, Creations, task panel) shares one instance.
 *
 * Format dispatch (hardened after the 08-03 founder round):
 *  - image/video/audio → native elements on the signed/proxied URL
 *  - pdf → UNsandboxed iframe (a sandboxed iframe blocks Chrome's PDF viewer
 *    — that was the grey sad-face)
 *  - md/txt/code → fetched and rendered IN the card (markdown through the
 *    app's own renderer, so .md finally reads like everything else)
 *  - html → sandboxed iframe via /v (rendered, never source)
 *  - xlsx/xls/csv → parsed in-browser with SheetJS into real scrollable
 *    tables; docx → mammoth into a readable document (both lazy-loaded, the
 *    same libraries the sandbox thumb lane renders with)
 *  - pptx and friends → honest card (nothing lightweight renders them) with
 *    the open/download escape hatches
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { renderMarkdown } from "@/lib/markdown";
import { sanitizePreviewHtml } from "@/lib/sanitize-preview-html";
import { viewerHref } from "@/lib/viewer-origin";
import { cn } from "@/lib/utils";
import { useShareDialogStore } from "@/stores/shareDialogStore";
import { useFilesStore } from "./filesStore";
import { KIND_COLOR, type FileKind } from "./FileTileCaption";
import { FileKindGlyph } from "@/components/icons/FileKindGlyph";
import { PreviewLoadingStarIcon } from "@/components/icons/PreviewLoadingStarIcon";
import { TrashIcon } from "@/components/icons/TrashIcon";
import { useDeleteCreation } from "@/components/creations/useDeleteCreation";
import type { PreviewTarget, PreviewShare } from "./preview-target";

/** The header's slim 26px action pills — the approved button round. */
const PILL =
  "inline-flex h-[26px] flex-none cursor-pointer items-center gap-1.5 rounded-full border border-border-default bg-bg-elevated px-3 text-3 font-medium text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg-default disabled:opacity-50";
const PILL_PRIMARY =
  "inline-flex h-[26px] flex-none cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-fg-default px-3 text-3 font-medium text-fg-inverse shadow-file-preview-overlay transition-opacity hover:opacity-90 disabled:opacity-50";

/**
 * Share: the one door to sharing (Train B). The overlay no longer publishes; it opens
 * the share dialog, where general access, people and the permanent /v link live.
 */
function ShareButton({
  share,
  target,
}: {
  share: PreviewShare;
  target: PreviewTarget;
}) {
  const open = useShareDialogStore((s) => s.open);
  return (
    <button
      type="button"
      onClick={() =>
        open({
          id: share.id,
          name: target.name,
          sizeBytes: target.meta?.sizeBytes ?? null,
          where: share.lane === "artifact" ? "Creations" : "Files",
        })
      }
      className={PILL_PRIMARY}
    >
      Share
    </button>
  );
}

/** Delete, for a creation only (the artifact lane): a file from the user's own
 *  files has its own delete. Deletes at once and closes this preview; Undo
 *  rides the toast. Last in the row, and a bare glyph on phones. */
function DeleteButton({ id }: { id: string }) {
  const deleteCreation = useDeleteCreation();
  return (
    <button
      type="button"
      aria-label="Delete"
      onClick={() => deleteCreation(id)}
      className={PILL}
    >
      <TrashIcon className="size-3 opacity-55" aria-hidden />
      <span className="max-sm:hidden">Delete</span>
    </button>
  );
}

const KIND_LABEL: Record<FileKind, string> = {
  report: "Report",
  page: "Page",
  doc: "Document",
  image: "Image",
  video: "Video",
  audio: "Audio",
  table: "Table",
  code: "Code",
  other: "File",
};

/** The header's tinted glyph tile: the kind's own color at whisper strength,
 *  derived with color-mix so one rule serves every kind and both themes. */
function KindTile({ kind, size = 40 }: { kind: FileKind; size?: number }) {
  return (
    <span
      className={`flex flex-none items-center justify-center rounded-[12px] border bg-current/9 border-current/22 ${KIND_COLOR[kind]}`}
      style={{
        width: size,
        height: size,
      }}
      aria-hidden
    >
      <FileKindGlyph kind={kind} className="h-4.5 w-4.5" />
    </span>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "3 Aug", with the year appended only when it isn't this year's. */
function formatDay(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return d.getFullYear() === new Date().getFullYear()
    ? day
    : `${day} ${d.getFullYear()}`;
}

const MARKDOWN_EXTS = new Set(["md", "mdx", "markdown"]);
const TEXT_EXTS = new Set([
  "txt",
  "log",
  "py",
  "js",
  "ts",
  "tsx",
  "jsx",
  "sh",
  "sql",
  "css",
  "json",
  "yaml",
  "yml",
  "xml",
]);
const HTML_EXTS = new Set(["html", "htm"]);
/** Parsed in-browser with SheetJS — csv reads as a table, not a wall of commas. */
const SHEET_EXTS = new Set(["xlsx", "xls", "csv"]);
const DOC_EXTS = new Set(["docx"]);
/** Sheets beyond this many rows are cut for the DOM's sake — Download has the rest. */
const SHEET_MAX_ROWS = 500;

/** The honest escape hatch — shown for formats nothing renders (pptx, binaries)
 *  and when an in-browser parse fails: type glyph, name, download. Never blank. */
function UnrenderableCard({
  target,
  message,
}: {
  target: PreviewTarget;
  message: string;
}) {
  return (
    <div className="flex w-full max-w-100 flex-col items-center gap-4 px-8 py-12 text-center">
      <KindTile kind={target.kind} size={48} />
      <span className="max-w-full truncate text-3.5 font-medium text-fg-default">
        {target.name}
      </span>
      <p className="text-3.5 leading-relaxed text-fg-muted">{message}</p>
      {target.downloadHref && (
        <a href={target.downloadHref} className={PILL_PRIMARY}>
          Download {target.ext ? `.${target.ext}` : "file"}
        </a>
      )}
    </div>
  );
}

const PANE_SHELL = "h-full w-full overflow-y-auto";

// ─── Preview content cache ──────────────────────────────────────────────────
// Panes read through React Query with an Infinity staleTime: a file's preview
// is fetched (and for office formats, PARSED) once per session, then serves
// instantly — re-opens and the folder-open prefetch below both hit the same
// cache. Keys are the source URL, so a re-signed URL misses to a fresh fetch.

const PREVIEW_CACHE = {
  staleTime: Infinity,
  gcTime: 30 * 60_000,
  retry: false,
} as const;

function textQuery(src: string) {
  return {
    queryKey: ["preview-text", src] as const,
    queryFn: async () => {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`${res.status}`);
      return res.text();
    },
    ...PREVIEW_CACHE,
  };
}

function sheetQuery(src: string) {
  return {
    queryKey: ["preview-sheet", src] as const,
    queryFn: async () => {
      const [res, XLSX] = await Promise.all([fetch(src), import("xlsx")]);
      if (!res.ok) throw new Error(`${res.status}`);
      const wb = XLSX.read(await res.arrayBuffer(), { type: "array" });
      return wb.SheetNames.map((name) => {
        const ws = wb.Sheets[name];
        const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1");
        const truncated = range.e.r > SHEET_MAX_ROWS;
        if (truncated) {
          range.e.r = SHEET_MAX_ROWS;
          ws["!ref"] = XLSX.utils.encode_range(range);
        }
        return {
          name,
          html: XLSX.utils.sheet_to_html(ws, { header: "", footer: "" }),
          truncated,
        };
      });
    },
    ...PREVIEW_CACHE,
  };
}

function docQuery(src: string) {
  return {
    queryKey: ["preview-doc", src] as const,
    queryFn: async () => {
      const [res, mammoth] = await Promise.all([fetch(src), import("mammoth")]);
      if (!res.ok) throw new Error(`${res.status}`);
      const out = await mammoth.convertToHtml({
        arrayBuffer: await res.arrayBuffer(),
      });
      return out.value;
    },
    ...PREVIEW_CACHE,
  };
}

/** Files a folder view warms per mount — enough to cover a screenful of
 *  tiles without turning every folder open into a download storm. */
const PREFETCH_LIMIT = 12;

/** Warm one file's preview into the cache (folder views call this on mount
 *  for their first rows) so clicking a tile opens instantly. Only the lanes
 *  that fetch content — media/pdf/html ride the browser's own cache. */
export function prefetchPreview(
  qc: QueryClient,
  target: Pick<PreviewTarget, "ext" | "viewUrl" | "mediaUrl">
): void {
  const src = target.viewUrl ?? target.mediaUrl;
  if (!src) return;
  if (SHEET_EXTS.has(target.ext)) void qc.prefetchQuery(sheetQuery(src));
  else if (DOC_EXTS.has(target.ext)) void qc.prefetchQuery(docQuery(src));
  else if (MARKDOWN_EXTS.has(target.ext) || TEXT_EXTS.has(target.ext))
    void qc.prefetchQuery(textQuery(src));
}

export function prefetchPreviews(
  qc: QueryClient,
  targets: Array<Pick<PreviewTarget, "ext" | "viewUrl" | "mediaUrl">>
): void {
  targets.slice(0, PREFETCH_LIMIT).forEach((t) => prefetchPreview(qc, t));
}

/** Loading skeleton: content-shaped shimmer (title + prose lines, or a table
 *  grid) instead of one dead pulse block. `animate-shimmer` sweeps the theme's
 *  own surface tokens, so it reads correctly on both themes. */
/** The file's identity — tinted kind tile, name, and its one-line story
 *  (kind · size · created day). ONE implementation for the overlay header
 *  and the Computer pane's Preview tab bar (founder 08-08: the bar's left
 *  carries title, type, metadata — beautifully). */
export function PreviewIdentity({ target }: { target: PreviewTarget }) {
  const kindColorClass = KIND_COLOR[target.kind];
  const day = target.meta?.createdAt ? formatDay(target.meta.createdAt) : null;
  const metaBits: Array<{ key: string; node: ReactNode }> = [
    {
      key: "kind",
      node: (
        <span className={`font-semibold ${kindColorClass}`}>
          {KIND_LABEL[target.kind]}
        </span>
      ),
    },
  ];
  if (target.meta?.sizeBytes)
    metaBits.push({ key: "size", node: formatSize(target.meta.sizeBytes) });
  if (day)
    metaBits.push({
      key: "day",
      node:
        target.meta?.origin === "agent"
          ? `Created ${day} by Bash`
          : `Added ${day}`,
    });
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
      <KindTile kind={target.kind} size={34} />
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-3.5 font-semibold tracking-[-0.01em] text-fg-default"
          title={target.name}
        >
          {target.name}
        </p>
        <p className="truncate text-3 text-fg-subtle">
          {metaBits.map((bit, i) => (
            <Fragment key={bit.key}>
              {i > 0 && (
                <span className="mx-1.5 text-fg-faint" aria-hidden>
                  ·
                </span>
              )}
              <span>{bit.node}</span>
            </Fragment>
          ))}
        </p>
      </div>
    </div>
  );
}

/** The file's action cluster — share, open, download, and a creation's
 *  delete — ONE implementation for the overlay header and the Computer pane's
 *  Preview tab (founder 08-08: the Preview surface carries the same actions
 *  the Knowledge preview has). Close is deliberately not here: each frame
 *  owns its own close affordance. */
export function PreviewActions({ target }: { target: PreviewTarget }) {
  const isHtml =
    HTML_EXTS.has(target.ext) ||
    ((target.kind === "page" || target.kind === "report") && !!target.viewUrl);
  // Consistency ruling (founder 08-03): a new tab shows the ACTUAL file —
  // the direct/signed URL — for everything except HTML, whose raw URL would
  // show source code (only /v renders it).
  // HTML opens on the viewer host when one is configured (see lib/viewer-origin).
  const rawOpenHref = isHtml
    ? (target.viewUrl ?? target.mediaUrl)
    : (target.mediaUrl ?? target.viewUrl ?? target.downloadHref);
  const openHref =
    isHtml && rawOpenHref ? viewerHref(rawOpenHref) : rawOpenHref;
  // A page made of text the app holds (targetFromMarkdown) lives at a data:
  // URL: browsers refuse to navigate a tab to one, so there is nothing to
  // Open; Download still works, but only with the name on the anchor.
  const virtual = !!target.downloadHref?.startsWith("data:");
  return (
    <div className="flex flex-none items-center gap-1.5">
      {/* No keys on Share and Delete: neither holds state, and the one id
          on both siblings is a duplicate key, which leaves the old Share
          behind as a second pill when the preview moves to another file. */}
      {target.share && <ShareButton share={target.share} target={target} />}
      {openHref && !virtual && (
        <a
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in new tab"
          className={PILL}
        >
          <span className="text-2.5 opacity-55" aria-hidden>
            ⧉
          </span>
          Open
        </a>
      )}
      {target.downloadHref && (
        // Phones: Open covers the need and narrow chrome can't fit three
        // pills — Download returns at sm.
        <a
          href={target.downloadHref}
          download={virtual ? target.name : undefined}
          className={`${PILL} max-sm:hidden`}
        >
          <span className="text-2.5 opacity-55" aria-hidden>
            ⤓
          </span>
          Download
        </a>
      )}
      {target.share?.lane === "artifact" && (
        <DeleteButton id={target.share.id} />
      )}
    </div>
  );
}

/** The one loading gesture for every preview kind — the Radiant mark
 *  breathing inside a soft gold halo (founder pick 08-08: a centered
 *  animation, never a fake page structure — page shapes are unknowable). */
function PreviewLoading() {
  return (
    <div
      data-testid="preview-loading"
      className="flex h-full w-full flex-col items-center justify-center gap-7"
      aria-label="Opening preview"
      role="status"
    >
      <div className="relative grid h-18 w-18 place-items-center">
        <div
          className="bg-file-preview-overlay-gradient animate-preview-halo motion-reduce:animate-none absolute -inset-3.5 rounded-full"
          aria-hidden
        />
        <PreviewLoadingStarIcon />
      </div>
      <span className="text-3.5 tracking-[0.01em] text-fg-subtle">
        Opening…
      </span>
    </div>
  );
}

/** Frames (html / pdf) and images fetch their content AFTER mount — hold the
 *  document skeleton until the element fires load, so the stage never sits
 *  blank (founder 08-08: loading states, done properly). The frame stays
 *  mounted at opacity-0 the whole time (the /v proxy contract and the tests
 *  see it immediately); load fades it in over the skeleton. */
function LoadedFrame({
  src,
  title,
  sandbox,
  testId,
}: {
  src: string;
  title: string;
  sandbox?: string;
  testId?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative h-full w-full">
      {!loaded && (
        <div className="absolute inset-0 overflow-hidden">
          <PreviewLoading />
        </div>
      )}
      <iframe
        src={src}
        title={title}
        {...(sandbox !== undefined ? { sandbox } : {})}
        {...(testId ? { "data-testid": testId } : {})}
        onLoad={() => setLoaded(true)}
        className={`h-full w-full bg-white transition-opacity duration-150 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

/** "16:9" → "16 / 9" (valid CSS `aspect-ratio`), or undefined when unparseable/
 *  absent. `aspect` is the ratio REQUESTED at generation time, never measured —
 *  the CLS fallback for width/height-less media (task-story/Outcome.tsx's
 *  stream cards use the same fallback for the same artifacts). */
function aspectRatioStyle(
  aspect: string | null | undefined
): string | undefined {
  const m = aspect?.match(/^(\d+):(\d+)$/);
  return m ? `${m[1]} / ${m[2]}` : undefined;
}

function LoadedImage({
  src,
  alt,
  width,
  height,
  aspect,
}: {
  src: string;
  alt: string;
  width?: number | null;
  height?: number | null;
  aspect?: string | null;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      {!loaded && (
        <div className="absolute inset-0">
          <PreviewLoading />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        width={width ?? undefined}
        height={height ?? undefined}
        onLoad={() => setLoaded(true)}
        style={
          width || height
            ? undefined
            : { aspectRatio: aspectRatioStyle(aspect) }
        }
        className={`max-h-full max-w-full object-contain transition-opacity duration-150 ${
          loaded ? "opacity-100" : "absolute opacity-0"
        }`}
      />
    </div>
  );
}

/** Spreadsheet pane: SheetJS parses the bytes in-browser into real tables —
 *  the same library the sandbox thumb lane screenshots with. Lazy-loaded, so
 *  the chunk only ever downloads when someone opens a sheet. */
function SheetPane({ target }: { target: PreviewTarget }) {
  const src = target.viewUrl ?? target.mediaUrl;
  const {
    data: sheets,
    isPending,
    isError,
  } = useQuery({ ...sheetQuery(src ?? ""), enabled: !!src });

  if (!src)
    return (
      <UnrenderableCard
        target={target}
        message="This file has no readable source right now."
      />
    );
  if (isPending) return <PreviewLoading />;
  if (isError || !sheets)
    return (
      <UnrenderableCard
        target={target}
        message="Couldn&rsquo;t read this spreadsheet — download it instead."
      />
    );
  return (
    <div data-testid="preview-sheet" className={PANE_SHELL}>
      <div className="px-6 py-5 [&_table]:w-max [&_table]:border-collapse [&_table]:text-3.5 [&_td]:whitespace-nowrap [&_td]:border [&_td]:border-border-default [&_td]:px-3 [&_td]:py-1.5 [&_td]:text-fg-default">
        {sheets.map((sheet) => (
          <div key={sheet.name} className="mb-8 last:mb-0">
            {sheets.length > 1 && (
              <p className="mb-2 text-3 font-semibold uppercase tracking-[0.08em] text-fg-muted">
                {sheet.name}
              </p>
            )}
            <div
              className="overflow-x-auto"
              dangerouslySetInnerHTML={{
                __html: sanitizePreviewHtml(sheet.html),
              }}
            />
            {sheet.truncated && (
              <p className="mt-2 text-3 text-fg-muted">
                Showing the first {SHEET_MAX_ROWS} rows — Download has the full
                sheet.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Word-document pane: mammoth converts the docx to clean HTML in-browser,
 *  typeset with the same prose styling the markdown pane uses. */
function DocPane({ target }: { target: PreviewTarget }) {
  const src = target.viewUrl ?? target.mediaUrl;
  const {
    data: html,
    isPending,
    isError,
  } = useQuery({ ...docQuery(src ?? ""), enabled: !!src });

  if (!src)
    return (
      <UnrenderableCard
        target={target}
        message="This file has no readable source right now."
      />
    );
  if (isPending) return <PreviewLoading />;
  if (isError || html === undefined)
    return (
      <UnrenderableCard
        target={target}
        message="Couldn&rsquo;t read this document — download it instead."
      />
    );
  return (
    <div className={PANE_SHELL}>
      <div
        data-testid="preview-doc"
        className="mx-auto max-w-[68ch] px-8 py-10 text-3.5 leading-relaxed text-fg-default [&_a]:underline [&_h1]:mb-4 [&_h1]:text-6.5 [&_h1]:font-semibold [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-5 [&_h2]:font-semibold [&_img]:max-w-full [&_li]:mt-1 [&_p]:mb-3 [&_table]:border-collapse [&_td]:border [&_td]:border-border-default [&_td]:px-3 [&_td]:py-1.5"
        dangerouslySetInnerHTML={{ __html: sanitizePreviewHtml(html) }}
      />
    </div>
  );
}

/** Read/code pane: fetches the file's text and renders it in place —
 *  markdown through the app's renderer, everything else as monospace. */
function TextPane({ target }: { target: PreviewTarget }) {
  const src = target.viewUrl ?? target.mediaUrl;
  const {
    data: text,
    isPending,
    isError,
  } = useQuery({ ...textQuery(src ?? ""), enabled: !!src });

  const errorCard = (
    <p className="px-8 text-center text-3.5 text-fg-muted">
      Couldn&rsquo;t load this file&rsquo;s contents just now.
    </p>
  );
  if (!src) return errorCard;
  if (isPending) return <PreviewLoading />;
  if (isError || text === undefined) return errorCard;
  const state = { text };
  return (
    <div className={PANE_SHELL}>
      {MARKDOWN_EXTS.has(target.ext) ? (
        <div
          data-testid="preview-markdown"
          className="mx-auto max-w-[68ch] px-8 pt-10 pb-[calc(var(--computer-dock-height)+16px)] text-3.5 leading-relaxed text-fg-default [&_a]:underline [&_h1]:mb-4 [&_h1]:text-6.5 [&_h1]:font-semibold [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-5 [&_h2]:font-semibold [&_li]:mt-1 [&_p]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded-12 [&_pre]:bg-bg-muted [&_pre]:p-4"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(state.text) }}
        />
      ) : (
        // pre-wrap, never x-scroll: transcript-style files with long
        // unwrapped lines were running off the card edge (founder 08-04).
        <pre
          data-testid="preview-text"
          className="whitespace-pre-wrap break-words px-8 py-8 font-mono text-4 text-3.5 leading-relaxed text-fg-default"
        >
          {state.text}
        </pre>
      )}
    </div>
  );
}

/** The rich per-kind renderer behind the universal preview — shared by the
 *  full-screen overlay and the Computer pane's Preview tab (split V1). Keys
 *  off the PreviewTarget itself (kind/ext/urls), never a re-derived filename
 *  (creation captions carry no extension). */
export function PreviewStage({ target }: { target: PreviewTarget }) {
  const isPdf = target.ext === "pdf";
  const isTextual = MARKDOWN_EXTS.has(target.ext) || TEXT_EXTS.has(target.ext);
  const isHtml =
    HTML_EXTS.has(target.ext) ||
    ((target.kind === "page" || target.kind === "report") && !!target.viewUrl);
  if (target.kind === "image" && target.mediaUrl) {
    return (
      <LoadedImage
        // Remount per URL: `loaded` is local state, so without this a click
        // to a new image would keep the previous one's "loaded" flag and
        // skip straight past the loading skeleton (founder report 09-23).
        key={target.mediaUrl}
        src={target.mediaUrl}
        alt={target.name}
        width={target.width}
        height={target.height}
        aspect={target.aspect}
      />
    );
  }
  if (target.kind === "video" && target.mediaUrl) {
    return (
      <video
        src={target.mediaUrl}
        controls
        autoPlay
        playsInline
        width={target.width ?? undefined}
        height={target.height ?? undefined}
        style={
          target.width || target.height
            ? undefined
            : { aspectRatio: aspectRatioStyle(target.aspect) }
        }
        className="max-h-full max-w-full"
        aria-label={target.name}
      />
    );
  }
  if (target.kind === "audio" && target.mediaUrl) {
    return (
      <div className="flex w-full max-w-130 flex-col items-center gap-5 px-8 py-12">
        <KindTile kind="audio" size={48} />
        <span className="max-w-full truncate text-3.5 font-medium text-fg-default">
          {target.name}
        </span>
        <audio
          src={target.mediaUrl}
          controls
          autoPlay
          className="w-full"
          aria-label={target.name}
        />
      </div>
    );
  }
  if (isPdf && (target.viewUrl || target.mediaUrl)) {
    // NO sandbox attribute: a sandboxed iframe blocks the browser's PDF
    // plugin — that rendered as the grey sad-face instead of the PDF.
    return (
      <LoadedFrame
        key={(target.viewUrl ?? target.mediaUrl)!}
        src={(target.viewUrl ?? target.mediaUrl)!}
        title={target.name}
        testId="preview-pdf"
      />
    );
  }
  if (SHEET_EXTS.has(target.ext)) {
    return <SheetPane target={target} />;
  }
  if (DOC_EXTS.has(target.ext)) {
    return <DocPane target={target} />;
  }
  if (isTextual) {
    return <TextPane target={target} />;
  }
  if (isHtml && target.viewUrl) {
    // User HTML: never `allow-same-origin`. With it, `allow-scripts` on a
    // same-origin frame is no sandbox at all (the page can reach the app's
    // cookies and /api). The /v route also sends a CSP sandbox, so the
    // document is opaque-origin wherever it is served. The escape token lets
    // a link open an ORDINARY tab (the route sends every link to one).
    return (
      <LoadedFrame
        key={target.viewUrl}
        src={viewerHref(target.viewUrl)}
        title={target.name}
        sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
    );
  }
  return (
    <UnrenderableCard
      target={target}
      message="Browsers can&rsquo;t render this format inline — open or download it instead."
    />
  );
}

export function FilePreviewOverlay() {
  const storeTarget = useFilesStore((s) => s.previewTarget);
  const closePreview = useFilesStore((s) => s.closePreview);

  // Preserve the target so the exit animation can run when storeTarget becomes null,
  // then unmount it after the animation completes (600ms matches DIALOG_OVERLAY_CL duration).
  const [activeTarget, setActiveTarget] = useState(storeTarget);
  useEffect(() => {
    if (storeTarget) {
      setActiveTarget(storeTarget);
    } else {
      const timer = setTimeout(() => setActiveTarget(null), 600);
      return () => clearTimeout(timer);
    }
  }, [storeTarget]);

  const target = activeTarget;

  // The file's story, one segment per fact the row actually knows.
  const metaBits = useMemo(() => {
    if (!target) return [];
    const kindColorClass = KIND_COLOR[target.kind];
    const day = target.meta?.createdAt
      ? formatDay(target.meta.createdAt)
      : null;
    const arr: Array<{ key: string; node: ReactNode }> = [
      {
        key: "kind",
        node: (
          <span className={`font-semibold ${kindColorClass}`}>
            {KIND_LABEL[target.kind]}
          </span>
        ),
      },
    ];
    if (target.meta?.sizeBytes) {
      arr.push({ key: "size", node: formatSize(target.meta.sizeBytes) });
    }
    if (day) {
      arr.push({
        key: "day",
        node:
          target.meta?.origin === "agent"
            ? `Created ${day} by Bash`
            : `Added ${day}`,
      });
    }
    return arr;
  }, [target]);

  if (!target) return null;

  const stage = <PreviewStage target={target} />;

  return (
    <Dialog
      open={!!storeTarget}
      onOpenChange={(open) => !open && closePreview()}
    >
      <DialogContent
        position="center-always"
        hideCloseButton
        data-testid="file-preview-overlay"
        aria-label={`Preview of ${target.name}`}
        className={cn(
          "absolute top-0 sm:top-10 !h-auto !max-h-none !max-w-none !p-0",
          "bottom-0 sm:bottom-10",
          "max-md:left-1/2 left-(--site-main-center-offset) translate-y-0",
          "w-full sm:w-(--site-main-center-width-mb) md:w-[min(1008px,_var(--site-main-center-width))]",
          "max-sm:rounded-t-none max-sm:rounded-b-none",
          "pointer-events-auto flex flex-col overflow-hidden"
        )}
      >
        {/* Metadata header: glyph tile, name, the file's story, actions. */}
        <div className="flex flex-none items-center gap-2.5 border-b border-border-subtle py-2.5 pl-3.5 pr-3 sm:gap-3.5 sm:py-4 sm:pl-5 sm:pr-4.5">
          <KindTile kind={target.kind} />
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-4 font-semibold tracking-[-0.01em] text-fg-default"
              title={target.name}
            >
              {target.name}
            </p>
            <p className="truncate text-3 text-fg-subtle">
              {metaBits.map((bit, i) => (
                <Fragment key={bit.key}>
                  {i > 0 && (
                    <span className="mx-1.5 text-fg-faint" aria-hidden>
                      ·
                    </span>
                  )}
                  <span>{bit.node}</span>
                </Fragment>
              ))}
            </p>
          </div>
          <div className="flex flex-none items-center gap-1.5">
            <PreviewActions target={target} />
            <span
              className="mx-0.5 h-3.5 w-px flex-none bg-border-default"
              aria-hidden
            />
            <button
              type="button"
              onClick={closePreview}
              aria-label="Close preview"
              className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-3.5 leading-none text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg-default sm:h-6.5 sm:w-6.5 sm:text-3.5"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content — the whole body of the card is the file. */}
        <div className="min-h-0 flex-1 overflow-hidden">
          <div className="flex h-full w-full items-center justify-center">
            {stage}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
